import { apiClient, ApiError } from '../../../lib/apiClient';
import type { AppUser, UpdateProfileRequest } from '../types/auth.types';

interface ChangePasswordResponse {
  message: string;
}

export const authService = {
  /**
   * Fetches the app-level user profile after a successful Auth0 login.
   * Returns roles, authorization status, and first-login flag in one call,
   * replacing the old separate /auth/is_first endpoint.
   *
   * Uses skipAuthErrorHandling so the caller can surface 403 messages
   * (suspended / unauthorized) without triggering a session clear.
   */
  login: async (accessToken: string): Promise<AppUser> => {
    try {
      return await apiClient<AppUser>('/auth/login', {
        headers: { Authorization: `Bearer ${accessToken}` },
        skipAuthErrorHandling: true,
      });
    } catch (error) {
      console.error('[AuthService] Error fetching user profile:', error);
      throw error instanceof ApiError
        ? error
        : new Error('Failed to fetch user profile');
    }
  },

  /**
   * Changes the user's password (called after first login when is_first: true).
   */
  changePassword: async (
    accessToken: string,
    newPassword: string
  ): Promise<ChangePasswordResponse> => {
    return apiClient<ChangePasswordResponse>('/auth/modify-password', {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { new_password: newPassword },
    });
  },

  /**
   * Updates the authenticated user's own profile (name, country, division).
   * Uses PATCH /users/me — email, status, and rol are not editable by the user.
   */
  updateProfile: async (
    accessToken: string,
    data: UpdateProfileRequest
  ): Promise<{ message: string }> => {
    return apiClient<{ message: string }>('/users/me', {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: data,
    });
  },
};
